import { base64ToText } from "@/lib/utf8";

export type JwtResult =
  | { ok: true; header: unknown; payload: unknown; alg: string | null }
  | { ok: false; error: string };

/** Strips a leading "Bearer " (any case) and all whitespace/newlines, as copied from an Authorization header. */
export function cleanToken(raw: string): string {
  return raw.trim().replace(/^bearer(\s+|$)/i, "").replace(/\s+/g, "");
}

function decodePart(part: string, name: "header" | "payload"): { value: unknown } | { error: string } {
  let json: string;
  try { json = base64ToText(part); }
  catch { return { error: `The ${name} is not valid Base64URL text, so it could not be decoded.` }; }
  try { return { value: JSON.parse(json) }; }
  catch { return { error: `The ${name} decoded to text, but it is not valid JSON.` }; }
}

/** Decodes (never verifies) a JWT. */
export function decodeJwt(raw: string): JwtResult | null {
  const token = cleanToken(raw);
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return { ok: false, error: `Invalid JWT format: expected 3 parts separated by dots, found ${parts.length}.` };
  const h = decodePart(parts[0], "header");
  if ("error" in h) return { ok: false, error: h.error };
  const p = decodePart(parts[1], "payload");
  if ("error" in p) return { ok: false, error: p.error };
  const alg = h.value && typeof h.value === "object" && typeof (h.value as Record<string, unknown>).alg === "string"
    ? ((h.value as Record<string, unknown>).alg as string) : null;
  return { ok: true, header: h.value, payload: p.value, alg };
}

export const isAlgNone = (alg: string | null): boolean => alg !== null && alg.trim().toLowerCase() === "none";

export type ClaimStatus = "expired" | "not-expired" | "not-yet-valid" | "active" | null;

/** Status badge for a time claim; `nowMs` is injectable for tests. */
export function claimStatus(key: string, epochSeconds: number, nowMs: number): ClaimStatus {
  const t = epochSeconds * 1000;
  if (key === "exp") return t < nowMs ? "expired" : "not-expired";
  if (key === "nbf") return t > nowMs ? "not-yet-valid" : "active";
  return null;
}
